import type { WorkItem } from '../types';

export type WorkItemOwnershipTone = 'self' | 'other' | 'unassigned';

type UnknownRecord = Record<string, unknown>;

export interface WorkItemOwnershipSummary {
  assigneeName: string | null;
  label: string;
  tone: WorkItemOwnershipTone;
  isAssignedToCurrentUser: boolean;
  isAssignedToOtherUser: boolean;
  isUnassigned: boolean;
  participantCount: number;
  assignedUserIds: number[];
  isSharedAssignment: boolean;
  teamName: string | null;
}

export interface WorkItemAssignedParticipant {
  id?: string | number | null;
  name: string;
  isPrimary?: boolean;
}

function normalizeNameCandidate(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function normalizeNumericId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function asUnknownRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as UnknownRecord
    : null;
}

function parsePossiblyJsonArray(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.startsWith('[')) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
}

function getUserIdFromRecord(record: UnknownRecord): number | null {
  const candidateKeys = ['user_id', 'userId', 'id', 'value', 'member_id', 'employee_id'];

  for (const key of candidateKeys) {
    const normalizedId = normalizeNumericId(record[key]);
    if (normalizedId != null) {
      return normalizedId;
    }
  }

  return null;
}

function getUserNameFromRecord(record: UnknownRecord): string | null {
  const joinedName = [record.first_name, record.last_name]
    .map(normalizeNameCandidate)
    .filter((value): value is string => Boolean(value))
    .join(' ')
    .trim();

  if (joinedName) {
    return joinedName;
  }

  const directNameKeys = ['name', 'full_name', 'display_name', 'label', 'user_name'];
  for (const key of directNameKeys) {
    const candidate = normalizeNameCandidate(record[key]);
    if (candidate) {
      return candidate;
    }
  }

  return normalizeNameCandidate(record.email);
}

function getWorkItemAssignedParticipantName(record: UnknownRecord): string | null {
  const fullName = [record.first_name, record.last_name]
    .map(normalizeNameCandidate)
    .filter((value): value is string => Boolean(value))
    .join(' ')
    .trim();

  if (fullName) {
    return fullName;
  }

  return getUserNameFromRecord(record);
}

function getCustomFields(item: WorkItem): UnknownRecord {
  return asUnknownRecord(item.custom_fields) ?? {};
}

export function getWorkItemAssignedTeamName(item: WorkItem): string | null {
  const customFields = getCustomFields(item);
  const directTeamRecord = asUnknownRecord(item.assigned_team);
  const customTeamRecord = asUnknownRecord(customFields.assigned_team);

  return [
    normalizeNameCandidate(item.assigned_team_name),
    normalizeNameCandidate(directTeamRecord?.name),
    normalizeNameCandidate(directTeamRecord?.label),
    normalizeNameCandidate(customFields.assigned_team_name),
    normalizeNameCandidate(customTeamRecord?.name),
    normalizeNameCandidate(customTeamRecord?.label),
    normalizeNameCandidate(customFields.team_name),
  ].find((value): value is string => Boolean(value)) ?? null;
}

export function getWorkItemAssignedUserIds(item: WorkItem): number[] {
  const customFields = getCustomFields(item);
  const candidateSources: unknown[] = [
    item.assigned_users,
    item.assigned_users_details,
    item.assignees,
    customFields.assigned_users,
    customFields.assigned_users_details,
    customFields.assigned_user_ids,
    customFields.participant_user_ids,
  ];

  const collectedIds = new Set<number>();

  candidateSources.forEach((source) => {
    parsePossiblyJsonArray(source).forEach((entry) => {
      const directId = normalizeNumericId(entry);
      if (directId != null) {
        collectedIds.add(directId);
        return;
      }

      const record = asUnknownRecord(entry);
      if (!record) {
        return;
      }

      const recordId = getUserIdFromRecord(record);
      if (recordId != null) {
        collectedIds.add(recordId);
      }
    });
  });

  const assignedToId = normalizeNumericId(item.assigned_to);
  if (assignedToId != null) {
    collectedIds.add(assignedToId);
  }

  return Array.from(collectedIds);
}

export function getWorkItemAssignedParticipants(item: WorkItem): WorkItemAssignedParticipant[] {
  const customFields = getCustomFields(item);
  const primaryAssigneeId = normalizeNumericId(item.assigned_to);
  const participants = new Map<string, WorkItemAssignedParticipant>();

  const registerParticipant = (participant?: WorkItemAssignedParticipant | null) => {
    const trimmedName = participant?.name?.trim();
    const participantId = participant?.id != null ? String(participant.id) : null;

    if (!trimmedName && !participantId) {
      return;
    }

    const participantKey = participantId || trimmedName!.toLowerCase();
    const existing = participants.get(participantKey);

    participants.set(participantKey, {
      id: existing?.id ?? participant?.id,
      name: trimmedName || existing?.name || (participantId ? `User #${participantId}` : 'Assigned user'),
      isPrimary: existing?.isPrimary || Boolean(participant?.isPrimary),
    });
  };

  const registerRecordParticipant = (record: UnknownRecord, fallbackIsPrimary = false) => {
    const recordId = getUserIdFromRecord(record);
    const recordName = getWorkItemAssignedParticipantName(record);
    registerParticipant({
      id: recordId ?? undefined,
      name: recordName || (recordId != null ? `User #${recordId}` : ''),
      isPrimary: fallbackIsPrimary || (primaryAssigneeId != null && recordId === primaryAssigneeId),
    });
  };

  (Array.isArray(item.assignees) ? item.assignees : []).forEach((assignee, index) => {
    registerParticipant({
      id: assignee.user_id,
      name: [assignee.first_name, assignee.last_name].map(normalizeNameCandidate).filter((value): value is string => Boolean(value)).join(' ').trim()
        || normalizeNameCandidate(assignee.email)
        || (assignee.user_id != null ? `User #${assignee.user_id}` : ''),
      isPrimary: (primaryAssigneeId != null && normalizeNumericId(assignee.user_id) === primaryAssigneeId) || index === 0,
    });
  });

  [
    item.assigned_users,
    item.assigned_users_details,
    customFields.assigned_users,
    customFields.assigned_users_details,
    customFields.assigned_user_ids,
    customFields.participant_user_ids,
  ].forEach((source) => {
    parsePossiblyJsonArray(source).forEach((entry) => {
      const directId = normalizeNumericId(entry);
      if (directId != null) {
        registerParticipant({
          id: directId,
          name: `User #${directId}`,
          isPrimary: primaryAssigneeId != null && directId === primaryAssigneeId,
        });
        return;
      }

      const record = asUnknownRecord(entry);
      if (!record) {
        return;
      }

      registerRecordParticipant(record);
    });
  });

  if (primaryAssigneeId != null || normalizeNameCandidate(item.assigned_to_name)) {
    registerParticipant({
      id: primaryAssigneeId ?? undefined,
      name: normalizeNameCandidate(item.assigned_to_name)
        || getWorkItemPrimaryAssigneeName(item)
        || (primaryAssigneeId != null ? `User #${primaryAssigneeId}` : ''),
      isPrimary: true,
    });
  }

  return Array.from(participants.values()).sort((left, right) => {
    if (left.isPrimary === right.isPrimary) {
      return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
    }

    return left.isPrimary ? -1 : 1;
  });
}

export function getWorkItemParticipantCount(item: WorkItem): number {
  const customFields = getCustomFields(item);
  const derivedParticipants = getWorkItemAssignedParticipants(item).length;
  const assigneeRecords = Array.isArray(item.assignees) ? item.assignees.length : 0;
  const assignedUsers = getWorkItemAssignedUserIds(item).length;
  const assignedUserDetails = parsePossiblyJsonArray(item.assigned_users_details).length;
  const customAssignedUserDetails = parsePossiblyJsonArray(customFields.assigned_users_details).length;
  const customAssignedUsers = parsePossiblyJsonArray(customFields.assigned_users).length;

  return Math.max(derivedParticipants, assignedUsers, assigneeRecords, assignedUserDetails, customAssignedUserDetails, customAssignedUsers);
}

function getSharedAssignmentLabel(options: {
  isAssignedToCurrentUser: boolean;
  participantCount: number;
  teamName: string | null;
  assigneeName: string | null;
}): string {
  const { isAssignedToCurrentUser, participantCount, teamName, assigneeName } = options;

  if (teamName) {
    return participantCount > 1
      ? `${teamName} · ${participantCount} participants`
      : `Assigned to ${teamName}`;
  }

  if (isAssignedToCurrentUser) {
    return participantCount > 1
      ? `Shared with you +${participantCount - 1}`
      : 'Assigned to you';
  }

  if (assigneeName && participantCount > 1) {
    return `${assigneeName} +${participantCount - 1}`;
  }

  if (participantCount > 1) {
    return `Shared appointment · ${participantCount} participants`;
  }

  return assigneeName ? `Assigned to ${assigneeName}` : 'Assigned';
}

export function getWorkItemAssigneeNameFromAssignmentRecord(item: WorkItem): string | null {
  const assignees = Array.isArray(item.assignees) ? item.assignees : [];
  if (assignees.length === 0) {
    return null;
  }

  const assignedToId = item.assigned_to != null ? Number(item.assigned_to) : null;
  const matchedAssignee = assignedToId != null
    ? assignees.find((assignee) => Number(assignee.user_id) === assignedToId) ?? assignees[0]
    : assignees[0];

  if (!matchedAssignee) {
    return null;
  }

  const fullName = [matchedAssignee.first_name, matchedAssignee.last_name]
    .map(normalizeNameCandidate)
    .filter((value): value is string => Boolean(value))
    .join(' ')
    .trim();

  return fullName || normalizeNameCandidate(matchedAssignee.email);
}

export function getWorkItemPrimaryAssigneeName(item: WorkItem): string | null {
  const customFields = getCustomFields(item);
  const assignedUserDetails = [
    ...parsePossiblyJsonArray(item.assigned_users_details),
    ...parsePossiblyJsonArray(customFields.assigned_users_details),
    ...parsePossiblyJsonArray(customFields.assigned_users),
  ]
    .map(asUnknownRecord)
    .filter((value): value is UnknownRecord => Boolean(value));

  const candidateNames = [
    normalizeNameCandidate(item.assigned_to_name),
    normalizeNameCandidate((item as WorkItem & { assignee_name?: string }).assignee_name),
    getWorkItemAssigneeNameFromAssignmentRecord(item),
    ...assignedUserDetails.map(getUserNameFromRecord),
    normalizeNameCandidate(customFields.assignee_name),
    normalizeNameCandidate(customFields.assigned_to_name),
    normalizeNameCandidate(customFields.assigned_user_name),
    normalizeNameCandidate(customFields.employee_name),
  ];

  return candidateNames.find((value): value is string => Boolean(value)) ?? null;
}

export function getWorkItemOwnershipSummary(item: WorkItem, currentUserId: number | null): WorkItemOwnershipSummary {
  const assigneeName = getWorkItemPrimaryAssigneeName(item);
  const assignedUserIds = getWorkItemAssignedUserIds(item);
  const participantCount = Math.max(getWorkItemParticipantCount(item), assignedUserIds.length, assigneeName ? 1 : 0);
  const teamName = getWorkItemAssignedTeamName(item);
  const hasAssignmentRecord = Array.isArray(item.assignees) && item.assignees.length > 0;
  const hasAssignment = participantCount > 0 || assigneeName !== null || hasAssignmentRecord || teamName !== null;
  const isAssignedToCurrentUser = currentUserId != null && assignedUserIds.includes(Number(currentUserId));
  const isAssignedToOtherUser = hasAssignment && !isAssignedToCurrentUser;
  const isSharedAssignment = participantCount > 1 || teamName !== null;
  const label = getSharedAssignmentLabel({
    isAssignedToCurrentUser,
    participantCount: Math.max(participantCount, isAssignedToCurrentUser ? 1 : 0),
    teamName,
    assigneeName,
  });

  if (isAssignedToCurrentUser) {
    return {
      assigneeName,
      label,
      tone: 'self',
      isAssignedToCurrentUser: true,
      isAssignedToOtherUser: false,
      isUnassigned: false,
      participantCount: Math.max(participantCount, 1),
      assignedUserIds,
      isSharedAssignment,
      teamName,
    };
  }

  if (isAssignedToOtherUser) {
    return {
      assigneeName,
      label,
      tone: 'other',
      isAssignedToCurrentUser: false,
      isAssignedToOtherUser: true,
      isUnassigned: false,
      participantCount,
      assignedUserIds,
      isSharedAssignment,
      teamName,
    };
  }

  return {
    assigneeName,
    label: 'Unassigned',
    tone: 'unassigned',
    isAssignedToCurrentUser: false,
    isAssignedToOtherUser: false,
    isUnassigned: true,
    participantCount: 0,
    assignedUserIds,
    isSharedAssignment: false,
    teamName,
  };
}
